/**
 * On-device debug console, enabled only by `?debug` in the URL.
 *
 * iOS Safari cannot be inspected from Windows without extra tooling, so a
 * device test otherwise reports failures as "the page just went white". This
 * panel puts the console, uncaught errors and failed asset loads on the screen
 * of the phone itself. It is deliberately dependency-free and touches nothing
 * outside its own element: no imports from the book, no shared CSS.
 *
 * Not loaded in normal use — `initDebugConsole()` is only called when the query
 * string carries `debug`.
 */

const MAX_LINES = 300;

function environmentLines() {
  const dpr = window.devicePixelRatio;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return [
    `ua      ${navigator.userAgent}`,
    `screen  ${window.screen.width}x${window.screen.height} · viewport ${window.innerWidth}x${window.innerHeight} · dpr ${dpr}`,
    `motion  ${reduced ? 'reduce' : 'no-preference'} · lang ${document.documentElement.lang || '—'} · url ${window.location.search || '—'}`
  ];
}

export function initDebugConsole() {
  if (document.querySelector('.book-debug')) return null;

  const panel = document.createElement('aside');
  panel.className = 'book-debug';
  panel.setAttribute('aria-label', 'Debug console');
  panel.style.cssText = `position:fixed; left:0; right:0; bottom:0; z-index:2147483647;
    max-height:42vh; display:flex; flex-direction:column; background:rgba(16,16,18,0.92);
    color:#e8e8e8; font:11px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;
    -webkit-backdrop-filter:blur(6px); backdrop-filter:blur(6px);`;

  const bar = document.createElement('div');
  bar.style.cssText = `display:flex; gap:8px; align-items:center; padding:6px 10px;
    border-bottom:1px solid rgba(255,255,255,0.15); flex:0 0 auto;`;

  const title = document.createElement('span');
  title.textContent = 'debug';
  title.style.cssText = 'opacity:0.6; flex:1 1 auto;';

  const log = document.createElement('div');
  log.style.cssText = 'overflow:auto; -webkit-overflow-scrolling:touch; padding:6px 10px; flex:1 1 auto;';

  const button = (label, onClick) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.style.cssText = `font:inherit; color:inherit; background:rgba(255,255,255,0.12);
      border:0; border-radius:4px; padding:4px 10px; min-height:28px;`;
    b.addEventListener('click', onClick);
    return b;
  };

  let collapsed = false;
  const toggle = button('hide', () => {
    collapsed = !collapsed;
    log.style.display = collapsed ? 'none' : '';
    toggle.textContent = collapsed ? 'show' : 'hide';
  });

  bar.append(title, button('clear', () => log.replaceChildren()), toggle);
  panel.append(bar, log);
  document.body.append(panel);

  let count = 0;
  function write(kind, parts) {
    const line = document.createElement('div');
    const colour = { error: '#ff8a80', warn: '#ffd180', info: '#8ab4ff' }[kind] ?? '#e8e8e8';
    line.style.cssText = `color:${colour}; white-space:pre-wrap; word-break:break-word; padding:1px 0;`;
    line.textContent = parts
      .map((p) => {
        if (typeof p === 'string') return p;
        if (p instanceof Error) return `${p.name}: ${p.message}`;
        try { return JSON.stringify(p); } catch { return String(p); }
      })
      .join(' ');
    const atBottom = log.scrollTop + log.clientHeight >= log.scrollHeight - 24;
    log.append(line);
    if (++count > MAX_LINES) { log.firstElementChild?.remove(); count--; }
    if (atBottom) log.scrollTop = log.scrollHeight;
    title.textContent = `debug · ${count}`;
  }

  for (const kind of ['log', 'info', 'warn', 'error']) {
    const original = console[kind].bind(console);
    console[kind] = (...args) => { write(kind === 'log' ? 'info' : kind, args); original(...args); };
  }

  window.addEventListener('error', (event) => {
    // Asset failures (an image or audio file that did not load) arrive as
    // capture-phase error events with a target instead of a message — on iOS
    // that is what a blank page usually turns out to be.
    const target = event.target;
    if (target && target !== window && target.tagName) {
      write('error', [`${target.tagName.toLowerCase()} failed: ${target.currentSrc || target.src || ''}`]);
      return;
    }
    write('error', [`${event.message} @ ${event.filename}:${event.lineno}`]);
  }, true);

  window.addEventListener('unhandledrejection', (event) => write('error', ['unhandled rejection:', event.reason]));

  for (const line of environmentLines()) write('info', [line]);
  window.addEventListener('resize', () => write('info', [`viewport ${window.innerWidth}x${window.innerHeight}`]), { passive: true });

  return panel;
}
